import React, { Component } from "react";
import { Button, Table, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { PREVIOUS_POSITION_API_URL, TRADES_API_URL } from "../constants";
import axios from "axios";
import { getAuthHeaders } from "../api";
import { getNetTradeProfit } from "../utils/tradeStats";
import TradeReplayPanel from "./TradeReplayPanel";

class PositionList extends Component {
  positionsPerPageOptions = [5, 10, 25, 50];

  state = {
    currentPage: 1,
    positionsPerPage: 5,
    positionInfo: {},
    detailView: "details",
    replayTimeframe: "H4",
    replayData: null,
    replayLoading: false,
    replayError: "",
  };

  openPositionInfo = (positionId) => {
    axios
      .get(`${PREVIOUS_POSITION_API_URL}?ticket=${positionId}`, getAuthHeaders())
      .then((res) =>
        this.setState({
          positionInfo: JSON.parse(res.data),
          detailView: "details",
          replayTimeframe: "H4",
          replayData: null,
          replayLoading: false,
          replayError: "",
        }),
      );
  };

  openReplayView = () => {
    const positionId = this.state.positionInfo.position_id || this.state.positionInfo.ticket;
    if (!positionId) {
      return;
    }

    this.setState(
      {
        detailView: "replay",
        replayLoading: true,
        replayError: "",
      },
      () => this.fetchReplayData(this.state.replayTimeframe),
    );
  };

  fetchReplayData = (timeframe) => {
    const positionId = this.state.positionInfo.position_id || this.state.positionInfo.ticket;
    if (!positionId) {
      return;
    }

    axios
      .get(`${TRADES_API_URL}${positionId}/replay/?timeframe=${timeframe}`, getAuthHeaders())
      .then((res) =>
        this.setState({
          replayData: res.data,
          replayLoading: false,
          replayError: "",
          replayTimeframe: timeframe,
          detailView: "replay",
        }),
      )
      .catch((error) =>
        this.setState({
          replayData: null,
          replayLoading: false,
          replayError: error.response?.data?.detail || "Unable to load replay data right now.",
          replayTimeframe: timeframe,
          detailView: "replay",
        }),
      );
  };

  getPositionChronologyTime = (position) => {
    return position.close_time || position.time_close || position.time_open || position.open_time || position.time || 0;
  };

  // Method to change the current page
  handlePageChange = (pageNumber) => {
    const totalPages = Math.ceil((this.props.positions || []).length / this.state.positionsPerPage);
    const nextPage = Math.max(1, Math.min(pageNumber, totalPages || 1));
    this.setState({ currentPage: nextPage });
  };

  handlePositionsPerPageChange = (event) => {
    const positionsPerPage = Number(event.target.value);
    if (!this.positionsPerPageOptions.includes(positionsPerPage)) {
      return;
    }

    this.setState({ positionsPerPage, currentPage: 1 });
  };

  // Fetch position information
  getPositionInfo = (e) => {
    this.openPositionInfo(e.currentTarget.id);
  };

  handleRowKeyDown = (e, positionId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.openPositionInfo(positionId);
    }
  };

  toggle = () => {
    this.setState({
      positionInfo: {},
      detailView: "details",
      replayTimeframe: "H4",
      replayData: null,
      replayLoading: false,
      replayError: "",
    });
  };

  showDetailsView = () => {
    this.setState({ detailView: "details" });
  };

  render() {
    const { positions, title = "Trades" } = this.props;
    const { currentPage, positionsPerPage } = this.state;
    const positionInfo = this.state.positionInfo;
    const { detailView, replayData, replayLoading, replayError, replayTimeframe } = this.state;
    const sortedPositions = [...positions].sort((a, b) => {
      const aTime = new Date(this.getPositionChronologyTime(a)).getTime();
      const bTime = new Date(this.getPositionChronologyTime(b)).getTime();
      return bTime - aTime;
    });
    const hasCloseTime = sortedPositions.some((position) => position.close_time || position.time_close);

    // Calculate the indices for the current page
    // Calculate total pages
    const totalPages = Math.ceil(sortedPositions.length / positionsPerPage);
    const indexOfLastPosition = currentPage * positionsPerPage;
    const indexOfFirstPosition = indexOfLastPosition - positionsPerPage;
    const currentPositions = sortedPositions.slice(indexOfFirstPosition, indexOfLastPosition);
    const emptyRowsCount = totalPages > 1 ? Math.max(0, positionsPerPage - currentPositions.length) : 0;

    // Helper function for order type
    const orderType = (positionType) => {
      return positionType === 0 ? "BUY" : "SELL";
    };

    const formatDateOnly = (dateValue) => {
      if (!dateValue) return "";
      const asString = String(dateValue);
      if (asString.includes(" ")) return asString.split(" ")[0];
      if (asString.includes("T")) return asString.split("T")[0];
      return asString;
    };

    const getValueClass = (value) => {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return "";
      if (numericValue > 0) return "text-success";
      if (numericValue < 0) return "text-danger";
      return "";
    };

    const hasValue = (value) => value !== null && value !== undefined && value !== "";

    const formatDetailValue = (value) => (hasValue(value) ? value : "N/A");

    // Render pagination items
    const paginationItems = [];
    for (let i = 1; i <= totalPages; i++) {
      paginationItems.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => this.handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <>
        <div className="trades-panel app-widget-panel">
          <div className="trades-panel__header">
            <h3 className="trades-panel__title app-widget-title">{title}</h3>
            <p className="trades-panel__hint">Click any trade to view details</p>
          </div>
          <div className="trades-table-shell">
            <Table hover className="table-algo_color trades-table text-center mb-0">
              <thead>
                <tr>
                  <th>Pair</th>
                  <th>{hasCloseTime ? "Close Time" : "Open Time"}</th>
                  <th>Type</th>
                  <th>Net P/L</th>
                </tr>
              </thead>
              <tbody>
                {!currentPositions || currentPositions.length <= 0 ? (
                  <tr>
                    <td colSpan="4" align="center" className="trades-table__empty">
                      <b>No Trades At The Moment</b>
                    </td>
                  </tr>
                ) : (
                  <>
                    {currentPositions.map((position) => (
                      <tr
                        className="trades-table__row trades-table__row--interactive"
                        id={position.position_id ? position.position_id : position.ticket}
                        onClick={this.getPositionInfo}
                        onKeyDown={(e) => this.handleRowKeyDown(e, position.position_id ? position.position_id : position.ticket)}
                        key={position.position_id || position.ticket}
                        tabIndex="0"
                        role="button"
                        aria-label={`View details for ${position.symbol} ${orderType(position.type)} trade`}
                      >
                        <td data-label="Pair">{position.symbol}</td>
                        <td data-label={hasCloseTime ? "Close Time" : "Open Time"}>{formatDateOnly(hasCloseTime
                          ? (position.close_time || position.time_close || position.time_open || position.time)
                          : (position.time_open || position.open_time || position.time || position.close_time || position.time_close)
                        )}</td>
                        <td data-label="Type">{orderType(position.type)}</td>
                        <td data-label="Net P/L" className={getValueClass(getNetTradeProfit(position))}>{getNetTradeProfit(position)}</td>
                      </tr>
                    ))}
                    {Array.from({ length: emptyRowsCount }).map((_, idx) => (
                      <tr key={`empty-row-${idx}`} className="table-placeholder-row">
                        <td colSpan="4">&nbsp;</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </Table>
          </div>
          <div className="trades-panel__footer">
            <label className="trades-page-size">
              <span>Show</span>
              <select
                className="trades-page-size__select"
                aria-label={`${title} per page`}
                value={positionsPerPage}
                onChange={this.handlePositionsPerPageChange}
              >
                {this.positionsPerPageOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <span>trades</span>
            </label>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="trade-pagination-wrap">
            <Pagination className="trade-pagination">
              <PaginationItem disabled={currentPage === 1}>
                <PaginationLink previous onClick={() => this.handlePageChange(currentPage - 1)}>
                  Previous
                </PaginationLink>
              </PaginationItem>
              {paginationItems}
              <PaginationItem disabled={currentPage === totalPages}>
                <PaginationLink next onClick={() => this.handlePageChange(currentPage + 1)}>
                  Next
                </PaginationLink>
              </PaginationItem>
            </Pagination>
          </div>
        )}

        {/* Position Info Section */}
        {Object.keys(positionInfo).length > 0 ? (
          <Modal
            isOpen={true}
            toggle={this.toggle}
            className={detailView === "replay" ? "modal-dark modal-dark--replay" : "modal-dark"}
            size={detailView === "replay" ? "xl" : undefined}
          >
            <ModalHeader toggle={this.toggle}>
              {detailView === "replay" ? "Trade Replay" : "Position Information"}
            </ModalHeader>
            <ModalBody>
              {detailView === "replay" ? (
                <TradeReplayPanel
                  replayData={replayData}
                  loading={replayLoading}
                  error={replayError}
                  timeframe={replayTimeframe}
                />
              ) : (
                <div className="trade-detail-grid">
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Position Number</span>
                    <span className="trade-detail-value">{positionInfo.position_id ? positionInfo.position_id : positionInfo.ticket}</span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Order Type</span>
                    <span className="trade-detail-value">{orderType(positionInfo.type)}</span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Symbol</span>
                    <span className="trade-detail-value">{positionInfo.symbol}</span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Strategy</span>
                    <span className="trade-detail-value">{positionInfo.magic}</span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Net Profit</span>
                    <span className={`trade-detail-value ${getValueClass(getNetTradeProfit(positionInfo))}`}>
                      {formatDetailValue(getNetTradeProfit(positionInfo))}
                    </span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Profit Before Costs</span>
                    <span className={`trade-detail-value ${getValueClass(positionInfo.profit)}`}>
                      {formatDetailValue(positionInfo.profit)}
                    </span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Swap</span>
                    <span className={`trade-detail-value ${getValueClass(positionInfo.swap)}`}>
                      {formatDetailValue(positionInfo.swap)}
                    </span>
                  </div>
                  {positionInfo.pips && (
                    <div className="trade-detail-card">
                      <span className="trade-detail-label">Pips</span>
                      <span className={`trade-detail-value ${getValueClass(positionInfo.pips)}`}>{positionInfo.pips}</span>
                    </div>
                  )}
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Risk Reward</span>
                    <span className="trade-detail-value">{positionInfo.risk_reward_ratio ? positionInfo.risk_reward_ratio : "N/A"}</span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Volume</span>
                    <span className="trade-detail-value">{positionInfo.volume}</span>
                  </div>
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Open Price</span>
                    <span className="trade-detail-value">{positionInfo.open_price ? positionInfo.open_price : positionInfo.price}</span>
                  </div>
                  {positionInfo.close_price && (
                    <div className="trade-detail-card">
                      <span className="trade-detail-label">Close Price</span>
                      <span className="trade-detail-value">{positionInfo.close_price}</span>
                    </div>
                  )}
                  {positionInfo.stop_loss !== 0 && (
                    <div className="trade-detail-card">
                      <span className="trade-detail-label">Initial Stop Loss</span>
                      <span className="trade-detail-value">{positionInfo.stop_loss}</span>
                    </div>
                  )}
                  {positionInfo.modified_sl && (
                    <div className="trade-detail-card">
                      <span className="trade-detail-label">Moved Stop Loss</span>
                      <span className="trade-detail-value">{positionInfo.modified_sl}</span>
                    </div>
                  )}
                  {positionInfo.take_profit !== 0 && (
                    <div className="trade-detail-card">
                      <span className="trade-detail-label">Take Profit</span>
                      <span className="trade-detail-value">{positionInfo.take_profit}</span>
                    </div>
                  )}
                  <div className="trade-detail-card">
                    <span className="trade-detail-label">Open Time</span>
                    <span className="trade-detail-value">{positionInfo.time_open ? positionInfo.time_open : positionInfo.time}</span>
                  </div>
                  {positionInfo.close_time && (
                    <div className="trade-detail-card">
                      <span className="trade-detail-label">Close Time</span>
                      <span className="trade-detail-value">{positionInfo.close_time}</span>
                    </div>
                  )}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              {detailView === "replay" ? (
                <>
                  <Button color="secondary" onClick={this.showDetailsView}>
                    Back To Details
                  </Button>
                  <Button color="secondary" onClick={this.toggle}>
                    Close
                  </Button>
                </>
              ) : (
                <>
                  {/* <Button color="secondary" className="trade-detail-replay-button" onClick={this.openReplayView}>
                    <svg
                      className="trade-detail-replay-button__icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 18h16M7 16V9m5 7V5m5 11v-4"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                    Replay Chart
                  </Button> */}
                  <Button color="secondary" onClick={this.toggle}>
                    Close
                  </Button>
                </>
              )}
            </ModalFooter>
          </Modal>
        ) : (
          <div></div>
        )}
      </>
    );
  }
}

export default PositionList;
